import React, { useCallback, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid
} from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import {
  FormWithRedirect,
  ReferenceArrayInput,
  SaveButton,
  useCreate,
  useQuery,
  Loading
} from 'react-admin';

import GridInput from '../../core/components/inputs/GridInput';
import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import Confirm from '../../core/components/forked/layout/Confirm';
import { Typography } from '@material-ui/core';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const styles = () => ({
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px',
    marginTop: '10px',
    height: '30px'
  },
  form: {
    '& > div': {
      paddingTop: '0px !important',
      paddingBottom: '0px !important'
    }
  },
  title: {
    flex: 1,
    paddingTop: '20px'
  }
});

const useStyles = makeStyles({
  loading: {
    height: 'calc(100vh - 230px)'
  }
});

const TermsRelationsGridComponent = props => {
  const { choices, fundTermId, ...rest } = props;
  const classes = useStyles();
  let { data, loading } = useQuery({
    type: 'getManyReference',
    resource: 'fundtermsrelations',
    payload: {
      target: 'fundTermId',
      id: fundTermId,
      sort: {
        order: 'ASC'
      }
    }
  });
  if (loading) return <Loading className={classes.loading} />;

  const existingRelationsFundIds = (data && data.map(e => e.fundId)) || [];
  const availableChoices = choices.filter(
    e => !existingRelationsFundIds.includes(e.id)
  );

  return (
    <GridInput
      {...rest}
      choices={availableChoices}
      optionValueLabel={'Fund ID'}
      optionTextLabel={'Fund Name'}
    />
  );
};

const AddFunds = props => {
  const { classes, fundTermId, onChange, ids, emptyComponent = false } = props;
  const [showDialog, setShowDialog] = useState(false);
  const [showAllFundsDialog, setShowAllFundsDialog] = useState(false);

  const [hasAllFundAction, setHasAllFundAction] = useState(false);
  const [hasAddExistingFundActions, setHasAddExistingFundActions] = useState(
    false
  );

  const [create] = useCreate('fundtermsrelations');
  const notifyServerError = useNotifyServerError();

  useEffect(() => {
    if (emptyComponent || ids) {
      const showAddAllFundAction = emptyComponent || ids.length === 0;
      const showAddExistingFundsAction =
        emptyComponent ||
        ids.length === 0 ||
        (ids.length > 0 && ids.indexOf(`${fundTermId}:*`) === -1) ||
        emptyComponent;
      setHasAllFundAction(showAddAllFundAction);
      setHasAddExistingFundActions(showAddExistingFundsAction);
    }
  }, [ids, fundTermId, emptyComponent]);

  const handleClose = useCallback(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  const handleOpen = useCallback(() => {
    setShowDialog(true);
  }, [setShowDialog]);

  const handleSubmit = async values => {
    const payload = values.funds.map(currentValue => {
      return {
        fundTermId: fundTermId,
        fundId: currentValue
      };
    });
    create(
      { payload: { data: payload } },
      {
        onSuccess: () => {
          setShowDialog(false);
          onChange();
        },
        onFailure: notifyServerError
      }
    );
  };

  const handleOpenAllFunds = useCallback(() => {
    setShowAllFundsDialog(true);
  }, [setShowAllFundsDialog]);

  const handleCloseAllFunds = useCallback(() => {
    setShowAllFundsDialog(false);
  }, [setShowAllFundsDialog]);

  const handleSubmitAllFunds = () => {
    const payload = [
      {
        fundTermId: fundTermId,
        fundId: '*'
      }
    ];
    create(
      { payload: { data: payload } },
      {
        onSuccess: () => {
          setShowAllFundsDialog(false);
          onChange();
        },
        onFailure: notifyServerError
      }
    );
  };

  const validateFunds = value => {
    if (Array.isArray(value) && value.length < 1) {
      return 'At least one Fund must be selected';
    }
    return undefined;
  };

  return (
    <>
      {/* Show grid label only if there are some records, otherwise Empty component will render title itself */}
      {ids && ids.length > 0 && (
        <Typography component="h5" variant="h5" classes={{ h5: classes.title }}>
          {'Related Funds'}
        </Typography>
      )}
      {hasAllFundAction && (
        <Button
          color="primary"
          onClick={handleOpenAllFunds}
          label="Add ALL Funds"
          className={classes.addButton}
          disableFocusRipple
        >
          Add All Funds
        </Button>
      )}
      {hasAddExistingFundActions && (
        <Button
          color="primary"
          onClick={handleOpen}
          label="Add Funds"
          className={classes.addButton}
          disableFocusRipple
        >
          Add Funds
        </Button>
      )}
      <FormWithRedirect
        initialValues={{ funds: [], fundId: null }}
        toolbar={null}
        // We override the redux-form name to avoid collision with the react-admin main form
        resource="funds"
        // We override the redux-form onSubmit prop to handle the submission ourselves
        save={handleSubmit}
        className={classes.form}
        variant="standard"
        margin="normal"
        render={({ handleSubmitWithRedirect, pristine, saving }) => (
          <Dialog
            fullWidth
            open={showDialog}
            aria-label={`Create fund`}
            maxWidth="sm"
            onClose={(event, reason) => {
              if (reason === 'backdropClick') return null;
              handleClose();
            }}
          >
            <DialogTitleWithCloseButton onClose={handleClose}>
              {'Add Fund to Fund Term'}
            </DialogTitleWithCloseButton>
            <DialogContent>
              <Grid container spacing={1} style={{ width: '100%' }}>
                <Grid item sm={12}>
                  <ReferenceArrayInput
                    label=""
                    optionText="Fund Name"
                    optionValue="Fund ID"
                    resource="funds"
                    reference="funds"
                    source="funds"
                    validate={[validateFunds]}
                    perPage={-1}
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <TermsRelationsGridComponent fundTermId={fundTermId} />
                  </ReferenceArrayInput>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <SaveButton
                handleSubmitWithRedirect={handleSubmitWithRedirect}
                label={'Add'}
                icon={<></>}
              />
              <Button
                onClick={handleClose}
                variant="outlined"
                color="primary"
                //disabled={submitting}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        )}
      />
      <Confirm
        isOpen={showAllFundsDialog}
        title="Add Fund to Fund Term"
        content="Add All Funds to Fund Term?"
        onConfirm={handleSubmitAllFunds}
        onClose={() => handleCloseAllFunds()}
      />
    </>
  );
};

export default withStyles(styles)(AddFunds);
